/*
<summary>
   This functional component is all about rendering the Tool BOM CSV
   import popup and submitting the selected file to the upload API.
   Developer: Aashish Singh, Created Date: 08-Sep-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useStore } from '../../../contexts/store-provider';
import {
  getToolBOMFactoryIdFromLocalStorage,
  getTenantIdFromLocalStorage,
  getUserId,
} from '../../../helpers/localstorage-helper';
import { IFormModalProps } from '../../../models/forms/IForm';
import { IImportToolBOM } from '../../../models/forms/IImportToolBOM';
import DefaultModal from '../../../shared-components/modal/default-modal';
import { formatMessage } from '../../../translations/format-message';
import ImportToolBOMForm from '../forms/import-tool-bom-form';

/**
* This functional component renders the CSV import popup for Tool BOM and
* submits the selected file to the upload service.
* @param props: Modal identifier and close handler
*/
const ImportToolBOM = (props: IFormModalProps) => {
  const initialValues: IImportToolBOM = { csvFile: null };
  const { toolBOMStore } = useStore();
  const { ImportToolBOMFileUploadService, fileUploadState, resetFileUploadState } =
    toolBOMStore;

  useEffect(() => {
    if (fileUploadState.success) {
      onModalClosed();
    }
  }, [fileUploadState.success]);

  useEffect(() => {
    if (fileUploadState.error) {
      if (fileUploadState.error === 'PartialSuccess') {
        toast.success(formatMessage('PartialSuccess'));
        onModalClosed();
      } else {
        toast.error(formatMessage(fileUploadState.error));
        resetFileUploadState();
      }
    }
  }, [fileUploadState.error]);

  const onSubmitForm = (values: IImportToolBOM) => {
    if (values.csvFile) {
      const factoryId = getToolBOMFactoryIdFromLocalStorage() || '';
      const createdBy = Number(getUserId()) || 0;
      const tenantId = Number(getTenantIdFromLocalStorage());
      ImportToolBOMFileUploadService(factoryId, createdBy, values.csvFile, tenantId);
    }
  };

  const onModalClosed = () => {
    resetFileUploadState();
    props.modalClosed();
  };

  return (
    <React.Fragment>
      <DefaultModal
        id={props.id}
        size="sm"
        className="modal-sm fancy-modal"
        addHeader="import_tool_bom"
        updateHeader="import_tool_bom"
        onModalClosed={onModalClosed}
        isLoading={fileUploadState.inProgress}
      >
        <ImportToolBOMForm
          initialValues={initialValues}
          id={props.id}
          submitHandler={onSubmitForm}
          onModalClosed={onModalClosed}
          isLoading={fileUploadState.inProgress}
        />
      </DefaultModal>
    </React.Fragment>
  );
};

export default observer(ImportToolBOM);
